import { useCallback, useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import LoadingSpinner from '../components/LoadingSpinner';
import { hrmsAPI } from '../services/api';

function formatTs(value) {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
}

export default function HrmsSync() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    hrmsAPI.syncStatus()
      .then(({ data }) => setStatus(data))
      .catch((err) => setError(err.response?.data?.error || 'Failed to load sync status'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const runSync = async () => {
    setSyncing(true);
    setMessage(null);
    setError(null);
    try {
      const { data } = await hrmsAPI.sync();
      setMessage(`Sync ${data.status} (run #${data.runId})`);
      load();
    } catch (err) {
      setError(err.response?.data?.error || 'Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  if (loading && !status) return <LoadingSpinner />;

  const last = status?.lastRun;

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">HRMS Sync</h1>
          <p className="text-slate-500 mt-1">Phase 1 connector status, manual pull, and dead-letter count</p>
        </div>
        <button
          type="button"
          onClick={runSync}
          disabled={syncing || !status?.syncEnabled}
          className="btn-primary flex items-center gap-2 self-start disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
          {syncing ? 'Syncing…' : 'Sync now'}
        </button>
      </div>

      {!status?.syncEnabled && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
          <strong>HRMS_SYNC_ENABLED</strong> is false. Configure HRMS env vars and enable sync to pull org/employee masters.
        </div>
      )}

      {message && (
        <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">{message}</div>
      )}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">{error}</div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card">
          <p className="text-sm text-slate-500">Sync enabled</p>
          <p className="text-xl font-semibold mt-1">{status?.syncEnabled ? 'Yes' : 'No'}</p>
        </div>
        <div className="card">
          <p className="text-sm text-slate-500">Last run</p>
          <p className="text-xl font-semibold mt-1">{last?.status || '—'}</p>
          <p className="text-xs text-slate-400 mt-1">{formatTs(last?.finished_at || last?.started_at)}</p>
        </div>
        <div className="card">
          <p className="text-sm text-slate-500">Unresolved dead letters</p>
          <p className="text-xl font-semibold mt-1">{status?.unresolvedDeadLetters ?? 0}</p>
        </div>
      </div>

      {last?.error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
          Last error: {last.error}
        </div>
      )}

      <div className="card overflow-x-auto">
        <h2 className="font-semibold text-slate-900 mb-3">Recent runs</h2>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-slate-500 border-b">
              <th className="py-2 pr-3">ID</th>
              <th className="py-2 pr-3">Trigger</th>
              <th className="py-2 pr-3">Status</th>
              <th className="py-2 pr-3">Started</th>
              <th className="py-2 pr-3">Finished</th>
            </tr>
          </thead>
          <tbody>
            {(status?.recentRuns || []).map((run) => (
              <tr key={run.id} className="border-b border-slate-100">
                <td className="py-2 pr-3">{run.id}</td>
                <td className="py-2 pr-3">{run.trigger}</td>
                <td className="py-2 pr-3">{run.status}</td>
                <td className="py-2 pr-3">{formatTs(run.started_at)}</td>
                <td className="py-2 pr-3">{formatTs(run.finished_at)}</td>
              </tr>
            ))}
            {!status?.recentRuns?.length && (
              <tr>
                <td colSpan={5} className="py-4 text-slate-400">No sync runs yet</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {last?.stats && (
        <div className="card">
          <h2 className="font-semibold text-slate-900 mb-2">Last run stats</h2>
          <pre className="text-xs bg-slate-50 p-3 rounded overflow-x-auto">
            {JSON.stringify(last.stats, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}
